import { InputBaseComponentProps } from "@mui/material/InputBase";
import { Locales } from "@simplyk/common";
import { Control, FieldValues, Path, RegisterOptions, UseFormSetValue } from "react-hook-form";

import { useTranslateField } from "./useTranslateField";

import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardInputLabel } from "@/dashboard-design-system/InputLabel/DashboardInputLabel";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";

interface TranslatedTextFieldProps<T extends FieldValues> {
  fieldName: Path<T>;
  defaultFieldValue: string | undefined | null;
  defaultTranslationFieldValue: string | undefined | null;
  setValue: UseFormSetValue<T>;
  translationLocale: Locales;
  label: string;
  rules?: RegisterOptions<FieldValues, string>[] | undefined;
  control: Control<T> | undefined;
  error?: boolean | undefined;
  errorMessage: string | undefined;
  dataTest: string;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  inputProps?: InputBaseComponentProps | undefined;
  skipTranslation?: boolean;
}

export function TranslatedTextField<T extends FieldValues>({
  fieldName,
  defaultFieldValue,
  defaultTranslationFieldValue,
  setValue,
  translationLocale,
  label,
  rules,
  control,
  error,
  errorMessage,
  dataTest,
  multiline,
  rows,
  maxRows,
  inputProps,
  skipTranslation = false,
}: TranslatedTextFieldProps<T>) {
  const { isTranslating } = useTranslateField({
    fieldName,
    defaultFieldValue,
    defaultTranslationFieldValue,
    setValue,
    translationLocale,
    skipTranslation,
    isPlainText: true,
  });

  return isTranslating ? (
    <DashboardInputLabel label={label}>
      <DashboardSkeleton
        variant="rounded"
        width="100%"
        sx={(theme) => ({ height: theme.spacing(multiline ? 12 : 4.5) })}
      />
    </DashboardInputLabel>
  ) : (
    <DashboardTextField
      label={label}
      rules={rules}
      control={control}
      name={fieldName}
      defaultValue={defaultTranslationFieldValue}
      error={error}
      errorMessage={errorMessage}
      data-test={dataTest}
      multiline={multiline}
      rows={rows}
      maxRows={maxRows}
      inputProps={inputProps}
    />
  );
}
